import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import {
  ColumnDef,
  ColumnResizeMode,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { Checkbox } from '@/components/ui/catalyst/checkbox';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@/components/ui/catalyst/dropdown';
import { Input } from '@/components/ui/catalyst/input';
import { cn } from '@/lib/utils';
import { cmsImportsApiClient } from '@/services/api/cmsImportsService';
import type {
  CmsImportCaseSchemaWithDetails,
  CmsImportedTaskFilterSchema,
  CmsImportTaskSchemaWithDetails,
} from '@/services/api/generated/cmsImportsService';

import { ImportActionBar } from './ImportActionBar';
import { ImportScopeSection } from './ImportScopeSection';
import { MatterCell } from './MatterCell';

interface CMSImportReadyPhaseProps {
  importId: string;
  firmId: string;
  savedFilters: CmsImportedTaskFilterSchema | null;
  onIngestComplete: () => void;
}

// Extended task type that includes case match info
interface TaskWithCaseMatch extends CmsImportTaskSchemaWithDetails {
  caseMatch?: CmsImportCaseSchemaWithDetails;
}

interface FilterOption {
  label: string;
  value: string;
}

export const CMSImportReadyPhase: React.FC<CMSImportReadyPhaseProps> = ({
  importId,
  firmId,
  savedFilters,
  onIngestComplete,
}) => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // ===== IMPORT SCOPE FILTERS (persisted to backend) =====
  // These filters determine what tasks will actually be imported
  const [importScope, setImportScope] = useState<CmsImportedTaskFilterSchema>({
    assignee_internal_ids: null,
    case_internal_ids: null,
    statuses: null,
  });
  const [hasUnsavedScopeChanges, setHasUnsavedScopeChanges] = useState(false);

  // ===== VIEW FILTERS (client-side only, for exploration) =====
  // These filters narrow down the display within the import scope
  const [matterFilter, setMatterFilter] = useState<
    'all' | 'matched' | 'unmatched'
  >('all');
  const [searchInput, setSearchInput] = useState('');

  // Track if we should poll matching progress
  const [shouldPollMatching, setShouldPollMatching] = useState(true);

  // ===== API QUERIES =====
  const { data: filterOptions, isLoading: filterOptionsLoading } =
    cmsImportsApiClient.useCmsImportFilterOptionsQuery({ id: importId });

  // Fetch tasks matching IMPORT SCOPE (backend filtering)
  const {
    data: tasksData,
    isLoading: tasksLoading,
    refetch: refetchTasks,
  } = cmsImportsApiClient.useCmsImportTasksQuery({
    id: importId,
    // Pass import scope filters to backend
    assigneeInternalIds: importScope.assignee_internal_ids?.length
      ? importScope.assignee_internal_ids
      : undefined,
    caseInternalIds: importScope.case_internal_ids?.length
      ? importScope.case_internal_ids
      : undefined,
    statuses: importScope.statuses?.length ? importScope.statuses : undefined,
  });

  const { data: casesData, refetch: refetchCases } =
    cmsImportsApiClient.useCmsImportCasesQuery({ id: importId });

  const { data: casesMissingData, refetch: refetchCasesMissing } =
    cmsImportsApiClient.useCmsImportCasesMissingMattersQuery({ id: importId });

  const { data: matchingProgress } =
    cmsImportsApiClient.useCmsImportMatchingProgressQuery(
      { id: importId },
      {
        pollingInterval: shouldPollMatching ? 3000 : undefined,
      },
    );

  // Track previous completed count to detect new matches
  const prevCompletedCasesRef = useRef(0);

  // Update polling state and refetch data when progress changes
  useEffect(() => {
    if (matchingProgress) {
      setShouldPollMatching(matchingProgress.pending_cases > 0);

      // Refetch data when new matches are completed
      if (matchingProgress.completed_cases > prevCompletedCasesRef.current) {
        refetchCases();
        refetchCasesMissing();
        refetchTasks();
        prevCompletedCasesRef.current = matchingProgress.completed_cases;
      }
    }
  }, [matchingProgress, refetchCases, refetchCasesMissing, refetchTasks]);

  // ===== MUTATIONS =====
  const [updateCaseMatch, { isLoading: isUpdatingMatch }] =
    cmsImportsApiClient.useCmsImportUpdateCaseMatchMutation();

  const [ingest, { isLoading: isIngesting }] =
    cmsImportsApiClient.useCmsImportIngestMutation();

  // ===== NORMALIZE DATA ARRAYS =====
  const tasks: CmsImportTaskSchemaWithDetails[] = useMemo(
    () => (Array.isArray(tasksData) ? tasksData : tasksData ? [tasksData] : []),
    [tasksData],
  );

  const cases: CmsImportCaseSchemaWithDetails[] = useMemo(
    () => (Array.isArray(casesData) ? casesData : casesData ? [casesData] : []),
    [casesData],
  );

  const casesMissing: CmsImportCaseSchemaWithDetails[] = useMemo(
    () =>
      Array.isArray(casesMissingData)
        ? casesMissingData
        : casesMissingData
          ? [casesMissingData]
          : [],
    [casesMissingData],
  );

  // Build a map from case internal_id to case match info
  const caseMatchMap = useMemo(() => {
    const map = new Map<string, CmsImportCaseSchemaWithDetails>();
    cases.forEach((c) => {
      map.set(c.cms_case.internal_id, c);
    });
    return map;
  }, [cases]);

  // Enrich tasks with case match info
  const enrichedTasks: TaskWithCaseMatch[] = useMemo(() => {
    return tasks.map((task) => {
      const caseInternalId = task.cms_task.internal_case_id;
      const caseMatch = caseInternalId
        ? caseMatchMap.get(caseInternalId)
        : undefined;
      return { ...task, caseMatch };
    });
  }, [tasks, caseMatchMap]);

  // Calculate importable vs skipped tasks (based on matter match status)
  const { importableTaskCount, skippedTaskCount } = useMemo(() => {
    let importable = 0;
    let skipped = 0;
    enrichedTasks.forEach((task) => {
      if (task.caseMatch?.matter_id) {
        importable += 1;
      } else {
        skipped += 1;
      }
    });
    return { importableTaskCount: importable, skippedTaskCount: skipped };
  }, [enrichedTasks]);

  // ===== APPLY VIEW FILTERS (client-side) =====
  const filteredTasks = useMemo(() => {
    let result = enrichedTasks;

    // Apply matter filter
    if (matterFilter === 'unmatched') {
      result = result.filter((task) => !task.caseMatch?.matter_id);
    } else if (matterFilter === 'matched') {
      result = result.filter((task) => task.caseMatch?.matter_id);
    }

    // Apply search filter
    if (searchInput.trim()) {
      const searchLower = searchInput.toLowerCase();
      result = result.filter((task) => {
        const title = task.cms_task.title?.toLowerCase() || '';
        const caseName = task.cms_task.case_name?.toLowerCase() || '';
        const assignee = task.cms_task.assignee_name?.toLowerCase() || '';
        return (
          title.includes(searchLower) ||
          caseName.includes(searchLower) ||
          assignee.includes(searchLower)
        );
      });
    }

    // Sort by CMS case name
    result = [...result].sort((a, b) => {
      const nameA = a.cms_task.case_name?.toLowerCase() || '';
      const nameB = b.cms_task.case_name?.toLowerCase() || '';
      return nameA.localeCompare(nameB);
    });

    return result;
  }, [enrichedTasks, matterFilter, searchInput]);

  // ===== CALLBACKS =====
  const handleScopeChange = useCallback(
    (filters: CmsImportedTaskFilterSchema) => {
      setImportScope(filters);
    },
    [],
  );

  const handleSaveMatch = useCallback(
    async (caseInternalId: string, matterId: string | null) => {
      const caseMatch = caseMatchMap.get(caseInternalId);
      if (!caseMatch) {
        toast.error('Case not found');
        return;
      }

      try {
        await updateCaseMatch({
          id: importId,
          updateCaseMatchRequestSchema: {
            cms_import_case_id: caseMatch.id as string,
            matter_id: matterId,
          },
        }).unwrap();

        toast.success('Match updated');
        refetchCases();
        refetchCasesMissing();
        refetchTasks();
      } catch (error: unknown) {
        const err = error as { data?: { detail?: string } };
        toast.error(err?.data?.detail || 'Failed to update match');
      }
    },
    [
      importId,
      caseMatchMap,
      updateCaseMatch,
      refetchCases,
      refetchCasesMissing,
      refetchTasks,
    ],
  );

  const handleImport = useCallback(async () => {
    if (hasUnsavedScopeChanges) {
      toast.error('Please save your import scope changes before importing');
      return;
    }

    try {
      const result = await ingest({ id: importId }).unwrap();
      toast.success(
        `Import complete: ${result.tasks_created} created, ${result.tasks_updated} updated`,
      );
      onIngestComplete();
    } catch (error: unknown) {
      const err = error as { data?: { detail?: string } };
      toast.error(err?.data?.detail || 'Failed to import tasks');
    }
  }, [importId, ingest, onIngestComplete, hasUnsavedScopeChanges]);

  const formatDate = useCallback((dateStr: string | null): string => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  // ===== COLUMN DEFINITIONS (view filters only) =====
  const columns = useMemo<ColumnDef<TaskWithCaseMatch>[]>(
    () => [
      {
        id: 'cms_case',
        accessorFn: (row) => row.cms_task.case_name,
        header: 'CMS Case',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span
            className="truncate text-sm"
            title={row.original.cms_task.case_name || ''}
          >
            {row.original.cms_task.case_name || '—'}
          </span>
        ),
        size: 180,
        minSize: 100,
        maxSize: 400,
      },
      {
        id: 'matter',
        // eslint-disable-next-line react/no-unstable-nested-components
        header: () => (
          <ViewColumnFilter
            label="Matter"
            options={[
              { label: 'All', value: 'all' },
              { label: 'Needs Assignment', value: 'unmatched' },
              { label: 'Matched', value: 'matched' },
            ]}
            selectedValues={matterFilter === 'all' ? [] : [matterFilter]}
            onSelectionChange={(values) => {
              if (values.length === 0 || values.includes('all')) {
                setMatterFilter('all');
              } else if (values.includes('unmatched')) {
                setMatterFilter('unmatched');
              } else if (values.includes('matched')) {
                setMatterFilter('matched');
              }
            }}
            singleSelect
          />
        ),
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <MatterCell
            task={row.original}
            matterId={(row.original.caseMatch?.matter_id as string) || null}
            matterName={row.original.caseMatch?.matter_name || null}
            firmId={firmId}
            onSaveMatch={handleSaveMatch}
            isSaving={isUpdatingMatch}
            matchConfidence={row.original.caseMatch?.match_confidence}
            matchReason={row.original.caseMatch?.match_reason}
            matchType={row.original.caseMatch?.match_type}
          />
        ),
        size: 280,
        minSize: 150,
        maxSize: 500,
      },
      {
        id: 'title',
        accessorFn: (row) => row.cms_task.title,
        header: 'Task Title',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span
            className="truncate text-sm"
            title={row.original.cms_task.title}
          >
            {row.original.cms_task.title}
          </span>
        ),
        size: 300,
        minSize: 150,
        maxSize: 600,
      },
      {
        id: 'assignee',
        accessorFn: (row) => row.cms_task.assignee_name,
        header: 'Assignee',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span
            className="truncate text-sm"
            title={row.original.cms_task.assignee_name || ''}
          >
            {row.original.cms_task.assignee_name || '—'}
          </span>
        ),
        size: 150,
        minSize: 80,
        maxSize: 300,
      },
      {
        id: 'due_date',
        accessorFn: (row) => row.cms_task.due_date,
        header: 'Due Date',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span className="text-sm">
            {formatDate(row.original.cms_task.due_date)}
          </span>
        ),
        size: 120,
        minSize: 80,
        maxSize: 200,
      },
      {
        id: 'status',
        accessorFn: (row) => row.cms_task.status,
        header: 'Status',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <span
            className="px-2 py-0.5 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800"
            title={row.original.cms_task.status}
          >
            {row.original.cms_task.status}
          </span>
        ),
        size: 100,
        minSize: 60,
        maxSize: 200,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matterFilter, firmId, handleSaveMatch, isUpdatingMatch, formatDate],
  );

  const [columnResizeMode] = useState<ColumnResizeMode>('onChange');

  const table = useReactTable({
    data: filteredTasks,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode,
    enableColumnResizing: true,
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: filteredTasks.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 44,
    overscan: 10,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
      : 0;

  const isLoading = tasksLoading || filterOptionsLoading;

  // Check if any view filters are active
  const hasActiveViewFilters =
    matterFilter !== 'all' || searchInput.trim() !== '';

  const clearAllViewFilters = useCallback(() => {
    setMatterFilter('all');
    setSearchInput('');
  }, []);

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Import Scope Section */}
      <ImportScopeSection
        importId={importId}
        filterOptions={filterOptions}
        savedFilters={savedFilters}
        taskCountInScope={tasks.length}
        onScopeChange={handleScopeChange}
        onHasUnsavedChangesChange={setHasUnsavedScopeChanges}
      />

      {/* Matching Progress Bar */}
      {matchingProgress && matchingProgress.total_cases > 0 && (
        <div className="flex-shrink-0 px-4 py-3 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Case Matching
              </span>
              {matchingProgress.pending_cases > 0 && (
                <svg
                  className="animate-spin h-3.5 w-3.5 text-blue-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-500 dark:text-zinc-400">
                {matchingProgress.completed_cases} of{' '}
                {matchingProgress.total_cases} cases
              </span>
              {casesMissing.length > 0 && (
                <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                  <ExclamationTriangleIcon className="h-4 w-4" />
                  <span className="text-sm">
                    {casesMissing.length} need
                    {casesMissing.length === 1 ? 's' : ''} assignment
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="h-2 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-500 ease-out',
                matchingProgress.pending_cases > 0
                  ? 'bg-blue-600'
                  : 'bg-green-500',
              )}
              style={{
                width: `${Math.round((matchingProgress.completed_cases / matchingProgress.total_cases) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Search and View Filter Bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-2 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          <Input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search tasks..."
            className="w-64"
          />
          <span className="text-sm text-zinc-500">
            {filteredTasks.length} of {tasks.length} task
            {tasks.length !== 1 ? 's' : ''}
          </span>
        </div>
        {hasActiveViewFilters && (
          <Button plain onClick={clearAllViewFilters} className="text-sm">
            Clear view filters
          </Button>
        )}
      </div>

      {/* Virtualized Table */}
      <div className="h-[60vh] bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 overflow-hidden">
        <div ref={tableContainerRef} className="overflow-auto h-full relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white/80 dark:bg-zinc-900/80 flex items-center justify-center z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent" />
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  Loading tasks...
                </p>
              </div>
            </div>
          )}

          <table
            style={{
              width: table.getCenterTotalSize(),
              tableLayout: 'fixed',
            }}
          >
            <thead className="sticky top-0 bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className="text-left px-3 py-2 font-semibold text-zinc-700 dark:text-zinc-300 text-xs relative group overflow-hidden"
                      style={{
                        width: header.getSize(),
                        minWidth: header.getSize(),
                        maxWidth: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                      )}
                      {header.column.getCanResize() && (
                        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                        <div
                          onDoubleClick={() => header.column.resetSize()}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={cn(
                            'absolute right-0 top-0 h-full w-4 -mr-2 cursor-col-resize select-none touch-none z-10',
                            'after:absolute after:right-2 after:top-0 after:h-full after:w-[3px]',
                            'after:bg-transparent hover:after:bg-blue-500 dark:hover:after:bg-blue-400',
                            header.column.getIsResizing() &&
                              'after:bg-blue-500 dark:after:bg-blue-400',
                          )}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              )}
              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index];

                if (!row) {
                  return (
                    <tr
                      key={`placeholder-${virtualRow.index}`}
                      className="h-[44px]"
                    >
                      {table.getAllColumns().map((column) => (
                        <td
                          key={column.id}
                          className="px-3 py-2 overflow-hidden"
                          style={{
                            width: column.getSize(),
                            minWidth: column.getSize(),
                            maxWidth: column.getSize(),
                          }}
                        >
                          <div className="animate-pulse bg-zinc-200 dark:bg-zinc-700 h-4 rounded" />
                        </td>
                      ))}
                    </tr>
                  );
                }

                return (
                  <tr
                    key={row.id}
                    className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 h-[44px]"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-3 py-2 overflow-hidden text-ellipsis"
                        style={{
                          width: cell.column.getSize(),
                          minWidth: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                      >
                        <div className="flex items-center h-full overflow-hidden">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
              {paddingBottom > 0 && (
                <tr>
                  <td style={{ height: `${paddingBottom}px` }} />
                </tr>
              )}
            </tbody>
          </table>

          {/* Empty state */}
          {!isLoading && filteredTasks.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <p className="text-zinc-500 dark:text-zinc-400">
                  No tasks found
                </p>
                {hasActiveViewFilters && (
                  <p className="text-sm text-zinc-400 dark:text-zinc-500 mt-1">
                    Try adjusting your view filters
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="flex-shrink-0">
        <ImportActionBar
          importableTaskCount={importableTaskCount}
          skippedTaskCount={skippedTaskCount}
          unmatchedCaseCount={casesMissing.length}
          onImport={handleImport}
          isImporting={isIngesting}
          hasUnsavedScopeChanges={hasUnsavedScopeChanges}
        />
      </div>
    </div>
  );
};

// View Column Filter Component (for client-side filtering within scope)
interface ViewColumnFilterProps {
  label: string;
  options: FilterOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  singleSelect?: boolean;
}

const ViewColumnFilter: React.FC<ViewColumnFilterProps> = ({
  label,
  options,
  selectedValues,
  onSelectionChange,
  singleSelect = false,
}) => {
  const isFiltered = selectedValues.length > 0;

  const handleToggle = (value: string) => {
    if (singleSelect) {
      if (selectedValues.includes(value)) {
        onSelectionChange([]);
      } else {
        onSelectionChange([value]);
      }
    } else if (selectedValues.includes(value)) {
      onSelectionChange(selectedValues.filter((v) => v !== value));
    } else {
      onSelectionChange([...selectedValues, value]);
    }
  };

  return (
    <Dropdown>
      <DropdownButton
        as={Button}
        plain
        className={cn(
          'flex items-center gap-1 font-semibold text-xs',
          isFiltered && 'text-purple-600 dark:text-purple-400',
        )}
      >
        {label}
        {isFiltered && (
          <Badge color="purple" className="ml-1">
            {selectedValues.length}
          </Badge>
        )}
      </DropdownButton>

      <DropdownMenu
        className="min-w-[200px] max-h-[300px] overflow-auto p-2"
        anchor="bottom start"
      >
        {options.map((option) => (
          <DropdownItem
            key={option.value}
            onClick={() => handleToggle(option.value)}
            className="w-full flex! flex-row items-center gap-2"
          >
            <Checkbox
              checked={selectedValues.includes(option.value)}
              onChange={() => handleToggle(option.value)}
              className="w-4 h-4 shrink-0"
            />
            <DropdownLabel className="truncate">{option.label}</DropdownLabel>
          </DropdownItem>
        ))}

        {isFiltered && (
          <DropdownItem
            onClick={() => onSelectionChange([])}
            className="w-full flex! flex-row justify-center mt-2 border-t border-zinc-200 dark:border-zinc-700 pt-2"
          >
            <DropdownLabel>Clear filter</DropdownLabel>
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};
